import React, { Children, useEffect, useState } from 'react';
import { Grid } from '@mui/material';
import { SortableSearchField } from '@biq/apex-ui';
import {
    ListItemType,
    SORT,
    sortableSearchListItemTableStyles,
} from './sortable-search-list-item-table-helper';
import { SortableSearchListItem } from './SortableSearchListItem';
import {
    ItemType,
    SortableListItemDataWithAction,
} from './sortable-search-list-item-helper';

export const SortableSearchListItemTable = ({
    data,
    type,
    handleActionsClick,
}: {
    data: Array<Record<string, string>>;
    type: ItemType;
    handleActionsClick: (
        dataWithAction: SortableListItemDataWithAction,
        index: number
    ) => void;
}): React.JSX.Element => {
    const [searchInput, setSearchInput] = useState<string>('');
    const [sortBy, setSortBy] = useState<SORT>(null);
    const [listItems, setListItems] = useState<ListItemType[]>([]);

    const transformToListItems = (
        listData: Array<Record<string, string>>
    ): ListItemType[] => {
        return listData.map((listItem, index) => {
            const key = Object.keys(listItem)[0];

            return {
                id: index,
                name: type === 'Field Mapping' ? key : listItem.name,
                item: (
                    <SortableSearchListItem
                        data={listItem}
                        type={type}
                        handleActionsClick={(itemDetail): void =>
                            handleActionsClick(itemDetail, index)
                        }
                    />
                ),
            };
        });
    };

    const filterData = (
        list: ListItemType[],
        filterValue?: string
    ): ListItemType[] => {
        return list.filter(
            (item) =>
                item?.name
                    ?.toLowerCase()
                    .includes(
                        filterValue?.toLowerCase() ?? searchInput?.toLowerCase()
                    ) || (filterValue ?? searchInput) === ''
        );
    };

    useEffect(() => {
        setListItems(filterData(transformToListItems(data)));
    }, [data]);

    useEffect(() => {
        if (sortBy === null) {
            setListItems(filterData(transformToListItems(data)));
        } else if (sortBy === 'asc') {
            const ascendingSortedData = [...listItems].sort(
                (a: any, b: any) => {
                    return a.name
                        .toLowerCase()
                        .localeCompare(b.name.toLowerCase());
                }
            );
            setListItems(filterData(ascendingSortedData));
        } else {
            const descendingSortedData = [...listItems].sort(
                (a: any, b: any) => {
                    return b.name
                        .toLowerCase()
                        .localeCompare(a.name.toLowerCase());
                }
            );
            setListItems(filterData(descendingSortedData));
        }
    }, [sortBy]);

    const handleSearchInputChange = (value: string): void => {
        setSearchInput(value);
        setListItems(filterData(transformToListItems(data), value));
    };

    const handleSortAscendingClick = (): void => {
        setSortBy('asc');
    };

    const handleSortDescendingClick = (): void => {
        setSortBy('desc');
    };

    const handleUnsortClick = (): void => {
        setSortBy(null);
    };

    const handleClearClick = (): void => {
        setSearchInput('');
        setListItems(filterData(transformToListItems(data), ''));
    };
    return (
        <Grid mt={2}>
            <SortableSearchField
                value={searchInput}
                onInputChange={handleSearchInputChange}
                onSortAscendingClick={handleSortAscendingClick}
                onSortDescendingClick={handleSortDescendingClick}
                onUnsortClick={handleUnsortClick}
                onClearClick={handleClearClick}
            />
            <Grid sx={sortableSearchListItemTableStyles.resultsContainer}>
                <Grid sx={sortableSearchListItemTableStyles.resultsText}>
                    Results for&nbsp;
                </Grid>
                <Grid sx={sortableSearchListItemTableStyles.boldResultsText}>
                    {searchInput}
                </Grid>
            </Grid>
            <Grid sx={sortableSearchListItemTableStyles.listContainerStyles}>
                {Children.toArray(listItems?.map((item) => item.item))}
            </Grid>
        </Grid>
    );
};
